import { useState } from "react";
import { motion } from "framer-motion";
import { useData } from '@happyspace/sdk/data';
import { Mail, MapPin, CheckCircle2 } from "lucide-react";
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";

export default function ContactUs() {
  const { insert, loading, error } = useData("contact_submissions");
  const [formData, setFormData] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    subject: "",
    message: "",
    agreeToContact: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, agreeToContact: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreeToContact) {
      alert("Please agree to be contacted regarding your inquiry.");
      return;
    }

    setSubmitError(null);
    try {
      await insert({
        name: formData.fullName,
        email: formData.emailAddress,
        phone: formData.phoneNumber || "Not provided",
        subject: formData.subject,
        message: formData.message,
        agreedToContact: formData.agreeToContact ? "Yes" : "No",
        submittedAt: new Date().toISOString(),
      });
      setSubmitted(true);
      setFormData({
        fullName: "",
        emailAddress: "",
        phoneNumber: "",
        subject: "",
        message: "",
        agreeToContact: false,
      });
    } catch (err) {
      setSubmitError(err instanceof Error && err.message ? "Your message could not be sent. Please try again. (" + err.message + ")" : "Your message could not be sent. Please try again.");
      console.error("Submission failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <CategoryHero
        title="Contact Us"
        placeholder="How can we help you?"
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-05-28/9fdfc4d4aa05.jpeg"
      />
      <NewsTicker />

      <div className="max-w-[1100px] w-[calc(100%-40px)] mx-auto px-4 py-6 md:py-8">
        {/* Header Section */}
        <div className="text-center max-w-2xl mx-auto mb-6">
          <motion.h2 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl font-extrabold text-[#082f6d] mb-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Get in Touch
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-gray-600 text-[14px] md:text-[15px] leading-relaxed"
          >
            Have a question, feedback, or want to list your business? We'd love to hear from you. Reach out and our team will assist you.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Left Column: Form */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
            >
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-10 px-4"
                >
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-[#51ba4f]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Message Sent!
                  </h3>
                  <div className="bg-green-50/50 border border-green-100 rounded-xl p-5 max-w-md mx-auto mb-8">
                    <p className="text-gray-600 text-[14px] leading-relaxed">
                      Thank you for contacting Dallas Desi Scoop! Our team will review your message and get back to you within 2–4 business days.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-bold px-6 py-2.5 rounded-lg transition-colors duration-200"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Section Header */}
                  <div className="border-b border-gray-100 pb-3">
                    <h3 className="text-lg font-bold text-gray-800" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      Basic Information
                    </h3>
                    <p className="text-gray-400 text-[12px]">Please fill in your details below.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full h-11 px-3.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] focus:outline-none focus:border-[#082f6d] focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="emailAddress"
                        required
                        value={formData.emailAddress}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full h-11 px-3.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] focus:outline-none focus:border-[#082f6d] focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                        Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                        className="w-full h-11 px-3.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] focus:outline-none focus:border-[#082f6d] focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Inquiry about business listing"
                        className="w-full h-11 px-3.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] focus:outline-none focus:border-[#082f6d] focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you…"
                      className="w-full p-3.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] focus:outline-none focus:border-[#082f6d] focus:bg-white transition-all resize-none"
                    />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start gap-2.5 pt-2">
                    <input
                      type="checkbox"
                      id="agreeToContact"
                      checked={formData.agreeToContact}
                      onChange={handleCheckboxChange}
                      required
                      className="mt-1 h-4.5 w-4.5 rounded border-gray-300 text-[#082f6d] focus:ring-[#082f6d]"
                    />
                    <label htmlFor="agreeToContact" className="text-[13px] text-gray-600 leading-relaxed cursor-pointer select-none">
                      I agree to be contacted by Dallas Scoop regarding my inquiry. <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {error && (
                    <div className="text-red-500 text-[13px] font-medium bg-red-50 border border-red-100 rounded-lg p-3">
                      {error.message}
                    </div>
                  )}

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3 mb-3">
                      {submitError}
                    </div>
                  )}
                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#51ba4f] hover:bg-[#43a041] disabled:bg-gray-300 text-white font-bold h-12 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm text-[15px]"
                  >
                    {loading ? (
                      <span>Sending Message...</span>
                    ) : (
                      <>
                        <span>📩 Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>

          {/* Right Column: Helpers & Info */}
          <div className="lg:col-span-4 space-y-6">
            {/* Quick Helper Card */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm"
            >
              <h4 className="font-bold text-[16px] text-gray-800 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Looking to add your Business or Event?
              </h4>
              <p className="text-gray-500 text-[13px] leading-relaxed mb-4">
                You can directly submit your business listing or local event using our dedicated submission forms.
              </p>
              <div className="flex gap-3">
                <a
                  href="/add-business"
                  className="text-[12px] font-bold text-[#082f6d] hover:text-[#f59e0b] transition-colors"
                >
                  Add Business →
                </a>
                <span className="text-gray-300">|</span>
                <a
                  href="/add-event"
                  className="text-[12px] font-bold text-[#082f6d] hover:text-[#f59e0b] transition-colors"
                >
                  Add Event →
                </a>
              </div>
            </motion.div>


          </div>
        </div>
      </div>
    </div>
  );
}
